import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Grid,
  Divider,
  Chip,
  Alert,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useParams } from "react-router-dom";
import MuPb from "./MuPb";
import useCommonGetInit from "../components/events/hooks/useCommonGetInit";
import { getErrorResponse } from "../utils2024/utils";
import useGetEventSettings from "../components/hooks/useGetEventSettings";

// Styled Components
const VisitorCard = styled(Card)(({ theme }) => ({
  maxWidth: 900,
  margin: theme.spacing(4, "auto"),
  borderRadius: theme.spacing(2),
  boxShadow: theme.shadows[8],
  overflow: "hidden",
  background: theme.palette.background.paper,
  transition: "transform 0.3s ease",
  "&:hover": {
    transform: "translateY(-5px)",
  },
  [theme.breakpoints.down("sm")]: {
    margin: theme.spacing(2, 1),
    borderRadius: theme.spacing(1),
  },
}));

const BannerContainer = styled(Box)(({ theme, banner }) => ({
  position: "relative",
  height: 0,
  paddingTop: "33.33%", // 3:1 aspect ratio
  background: banner
    ? `linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.3)), url(${banner})`
    : theme.palette.grey[200],
  backgroundSize: "cover",
  backgroundPosition: "center",
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: theme.palette.common.white,
  textShadow: "0 2px 4px rgba(0,0,0,0.5)",
}));

const ProfileImageContainer = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(-6),
  left: "50%",
  transform: "translateX(-50%)",
  zIndex: 1,
  [theme.breakpoints.up("sm")]: {
    top: theme.spacing(-8),
    left: theme.spacing(4),
    transform: "none",
  },
}));

const ProfileAvatar = styled(Avatar)(({ theme }) => ({
  width: 120,
  height: 120,
  border: `5px solid ${theme.palette.background.paper}`,
  boxShadow: theme.shadows[4],
  transition: "transform 0.3s ease",
  "&:hover": {
    transform: "scale(1.05)",
  },
  [theme.breakpoints.down("sm")]: {
    width: 100,
    height: 100,
  },
}));

const NameTypography = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  color: theme.palette.primary.dark,
  marginBottom: theme.spacing(0.5),
  textAlign: "center",
  [theme.breakpoints.up("sm")]: {
    textAlign: "left",
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.secondary,
  fontSize: "0.9rem",
  textTransform: "uppercase",
  marginBottom: theme.spacing(1),
}));

const InfoTypography = styled(Typography)(({ theme }) => ({
  fontWeight: 400,
  color: theme.palette.text.primary,
  fontSize: "1rem",
  wordBreak: "break-word",
}));

const QRCodeContainer = styled(Box)(({ theme }) => ({
  background: theme.palette.grey[100],
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  textAlign: "center",
  boxShadow: theme.shadows[2],
  transition: "transform 0.3s ease, box-shadow 0.3s ease",
  margin: theme.spacing(2, "auto"),
  maxWidth: 220,
  "&:hover": {
    transform: "translateY(-3px)",
    boxShadow: theme.shadows[6],
  },
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  borderColor: theme.palette.primary.light,
  color: theme.palette.primary.main,
  transition: "background-color 0.3s ease",
  "&:hover": {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.common.white,
  },
}));

const StyledDivider = styled(Divider)(({ theme }) => ({
  margin: theme.spacing(3, 0),
  backgroundColor: theme.palette.grey[300],
}));

const VisitorCardView = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    data: eventData,
    isLoading: isEventLoading,
    error: loadError,
  } = useGetEventSettings();
  const { id } = useParams();
  const { data, isLoading, isError, error } = useCommonGetInit(
    `user/get-user-card-tmp?dB=dmtx2025-imtma&id=${id}`
  );

  if (isLoading || isEventLoading) {
    return <MuPb />;
  }

  if (isError || !data) {
    return (
      <Alert severity="error">
        {error ? getErrorResponse(error) : "Failed to load visitor data"}
      </Alert>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: "grey.100",
        minHeight: "100vh",
        py: 4,
        px: isMobile ? 0 : 4,
      }}
    >
      <VisitorCard>
        <BannerContainer banner={data.profileBanner || eventData.eventBanner} />

        <Box sx={{ position: "relative" }}>
          <ProfileImageContainer>
            <ProfileAvatar src={data.profileImage} alt={data.firstName}>
              {data.firstName?.charAt(0)}
              {data.lastName?.charAt(0)}
            </ProfileAvatar>
          </ProfileImageContainer>

          <CardContent
            sx={{ pt: isMobile ? 10 : 8, px: isMobile ? 2 : 4, pb: 4 }}
          >
            {/* Name and Company */}
            <Box sx={{ textAlign: isMobile ? "center" : "left", mb: 3 }}>
              <NameTypography variant={isMobile ? "h5" : "h4"}>
                {data.title} {data.firstName} {data.lastName}
              </NameTypography>
              <InfoTypography variant="subtitle1" color="text.secondary">
                {data.companyName}
              </InfoTypography>
            </Box>

            {/* Centered QR Code (mobile) or right-aligned (desktop) */}
            <Box
              sx={{
                display: "flex",
                justifyContent: isMobile ? "center" : "flex-end",
                mb: 3,
              }}
            >
              <QRCodeContainer>
                <img
                  src={data.qrLink}
                  alt="QR Code"
                  style={{
                    width: "100%",
                    maxWidth: 180,
                    height: "auto",
                    borderRadius: 8,
                  }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, display: "block" }}
                >
                  Scan to connect
                </Typography>
              </QRCodeContainer>
            </Box>

            <StyledDivider />

            <Grid container spacing={isMobile ? 1 : 3}>
              <Grid item xs={12} sm={6}>
                <SectionTitle>Username</SectionTitle>
                <InfoTypography>{data.username}</InfoTypography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <SectionTitle>Email</SectionTitle>
                <InfoTypography>{data.email}</InfoTypography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <SectionTitle>Mobile No</SectionTitle>
                <InfoTypography>{data.mobileNo}</InfoTypography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <SectionTitle>Visitor Group</SectionTitle>
                <InfoTypography>{data.visitorGroup}</InfoTypography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <SectionTitle>Industry Sector</SectionTitle>
                <InfoTypography>{data.industrySector}</InfoTypography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <SectionTitle>Location</SectionTitle>
                <InfoTypography>
                  {data?.city?.name}, {data?.state?.name}, {data?.country?.name}
                </InfoTypography>
              </Grid>
              <Grid item xs={12}>
                <SectionTitle>
                  How did you find out about this event?
                </SectionTitle>
                <InfoTypography>
                  {data.howDidYouFindOutAboutThisEvent}
                </InfoTypography>
              </Grid>
              <Grid item xs={12}>
                <SectionTitle>Product Categories</SectionTitle>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    justifyContent: isMobile ? "center" : "flex-start",
                  }}
                >
                  {data.productCategories?.map((category, index) => (
                    <StyledChip
                      key={index}
                      label={`${category.MainCategory}: ${category.Category}`}
                      variant="outlined"
                      size={isMobile ? "small" : "medium"}
                    />
                  ))}
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Box>
      </VisitorCard>
    </Box>
  );
};

export default VisitorCardView;
